import React, { useState, useEffect } from 'react';
import { adminEmailAPI } from '../../lib/api';
import {
  Mail, Edit2, ToggleLeft, ToggleRight, Plus, Trash2, X,
  Save, Send, Eye, Code, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const TRIGGER_LABELS = {
  registration: 'On Registration',
  payment_success: 'On Payment',
  referral_commission_earned: 'On Commission Earned',
  commission_approved: 'On Commission Approved',
  payout_processed: 'On Payout Processed',
  team_invite_accepted: 'On Team Invite Accepted',
  custom: 'Custom Trigger',
};

const AdminEmailNotificationsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toggling, setToggling] = useState('');
  const [testing, setTesting] = useState('');

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await adminEmailAPI.getAll();
      setTemplates(res.data.templates || []);
    } catch { toast.error('Failed to load email templates'); }
    finally { setLoading(false); }
  };

  const handleToggle = async (key) => {
    setToggling(key);
    try {
      const res = await adminEmailAPI.toggle(key);
      toast.success(res.data.enabled ? 'Template enabled' : 'Template disabled');
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Toggle failed'); }
    finally { setToggling(''); }
  };

  const handleTest = async (key) => {
    setTesting(key);
    try {
      const res = await adminEmailAPI.test(key);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send test email'); }
    finally { setTesting(''); }
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Delete this custom template?')) return;
    try {
      await adminEmailAPI.delete(key);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Email Notifications</h1>
          <p className="text-[#5F6368]">Manage and customize email notification templates</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-google flex items-center gap-2" data-testid="create-template-btn">
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {/* Templates List */}
      <div className="space-y-3" data-testid="email-templates-list">
        {templates.map(tpl => (
          <div key={tpl.key} className="dashboard-card" data-testid={`template-${tpl.key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tpl.enabled ? 'bg-[#E8F0FE]' : 'bg-[#F1F3F4]'}`}>
                  <Mail className={`w-5 h-5 ${tpl.enabled ? 'text-[#1A73E8]' : 'text-[#9AA0A6]'}`} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-[#202124] truncate">{tpl.name}</h3>
                    <span className="text-[10px] px-2 py-0.5 bg-[#F1F3F4] text-[#5F6368] rounded-full font-medium flex-shrink-0">
                      {TRIGGER_LABELS[tpl.trigger] || tpl.trigger}
                    </span>
                    {tpl.is_custom && (
                      <span className="text-[10px] px-2 py-0.5 bg-[#E8F0FE] text-[#1A73E8] rounded-full font-medium flex-shrink-0">
                        Custom
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#5F6368] truncate mt-0.5">{tpl.description}</p>
                  <p className="text-xs text-[#9AA0A6] mt-0.5 truncate">Subject: {tpl.subject}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                <button
                  onClick={() => handleTest(tpl.key)}
                  disabled={testing === tpl.key || !tpl.enabled}
                  className="p-2 text-[#5F6368] hover:bg-[#F1F3F4] rounded-lg transition-colors disabled:opacity-40"
                  title="Send test email"
                  data-testid={`test-${tpl.key}`}
                >
                  {testing === tpl.key ? <span className="animate-spin w-4 h-4 border-2 border-[#1A73E8] border-t-transparent rounded-full inline-block" /> : <Send className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setEditingTemplate(tpl)}
                  className="p-2 text-[#5F6368] hover:bg-[#E8F0FE] rounded-lg transition-colors"
                  title="Edit template"
                  data-testid={`edit-${tpl.key}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {tpl.is_custom && (
                  <button
                    onClick={() => handleDelete(tpl.key)}
                    className="p-2 text-[#D93025] hover:bg-[#FCE8E6] rounded-lg transition-colors"
                    title="Delete template"
                    data-testid={`delete-${tpl.key}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleToggle(tpl.key)}
                  disabled={toggling === tpl.key}
                  className="p-1"
                  title={tpl.enabled ? 'Disable' : 'Enable'}
                  data-testid={`toggle-${tpl.key}`}
                >
                  {tpl.enabled ? <ToggleRight className="w-8 h-8 text-[#1E8E3E]" /> : <ToggleLeft className="w-8 h-8 text-[#DADCE0]" />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Variables Reference */}
      <div className="dashboard-card">
        <h3 className="text-sm font-semibold text-[#202124] mb-2" style={{ fontFamily: 'Outfit' }}>
          <Code className="w-4 h-4 inline mr-1.5 text-[#1A73E8]" />Available Variables
        </h3>
        <p className="text-xs text-[#5F6368] mb-2">Use these in subject or body with double curly braces: {'{{variable_name}}'}</p>
        <div className="flex flex-wrap gap-1.5">
          {['app_name', 'app_url', 'user_name', 'user_email', 'plan_name', 'currency', 'amount', 'date', 'referred_name', 'commission_type', 'commission_amount', 'method', 'owner_name', 'member_name', 'member_email', 'role', 'access_type'].map(v => (
            <span key={v} className="text-xs px-2 py-1 bg-[#F1F3F4] text-[#5F6368] rounded font-mono">{`{{${v}}}`}</span>
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      {editingTemplate && (
        <EditTemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSuccess={() => { setEditingTemplate(null); fetchTemplates(); }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateTemplateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); fetchTemplates(); }}
        />
      )}
    </div>
  );
};

// --- Edit Template Modal ---
const EditTemplateModal = ({ template, onClose, onSuccess }) => {
  const [form, setForm] = useState({ ...template });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminEmailAPI.update(template.key, form);
      toast.success('Template updated');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="edit-template-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-template-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#DADCE0]">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            <Edit2 className="w-5 h-5 inline mr-2 text-[#1A73E8]" />Edit: {template.name}
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreviewMode(!previewMode)} className={`p-2 rounded-lg transition-colors ${previewMode ? 'bg-[#E8F0FE] text-[#1A73E8]' : 'text-[#5F6368] hover:bg-[#F1F3F4]'}`} data-testid="preview-toggle">
              <Eye className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F1F3F4] flex items-center justify-center" data-testid="edit-template-close">
              <X className="w-4 h-4 text-[#5F6368]" />
            </button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Name</label>
              <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="input-google text-sm" data-testid="edit-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Description</label>
              <input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} className="input-google text-sm" data-testid="edit-description" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Subject</label>
            <input value={form.subject || ''} onChange={e => setForm({ ...form, subject: e.target.value })} className="input-google text-sm" data-testid="edit-subject" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Variables (comma-separated)</label>
            <input
              value={(form.variables || []).join(', ')}
              onChange={e => setForm({ ...form, variables: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
              className="input-google text-sm font-mono"
              data-testid="edit-variables"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-[#5F6368]">{previewMode ? 'Preview' : 'Body (HTML)'}</label>
            </div>
            {previewMode ? (
              <div className="border border-[#DADCE0] rounded-lg p-4 bg-white min-h-[200px]" dangerouslySetInnerHTML={{ __html: form.body || '' }} data-testid="edit-preview" />
            ) : (
              <textarea
                value={form.body || ''}
                onChange={e => setForm({ ...form, body: e.target.value })}
                className="input-google text-sm font-mono min-h-[250px] resize-y"
                data-testid="edit-body"
              />
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 bg-[#F1F3F4] text-[#5F6368] rounded-lg text-sm font-medium hover:bg-[#E8EAED] transition-colors">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 btn-google flex items-center justify-center gap-2" data-testid="save-template-btn">
              {saving ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Saving...</> : <><Save className="w-4 h-4" /> Save Changes</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Create Template Modal ---
const CreateTemplateModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ key: '', name: '', description: '', subject: '', body: '', variables: [], enabled: true });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.key || !form.name || !form.subject) {
      toast.error('Key, name, and subject are required');
      return;
    }
    setSaving(true);
    try {
      await adminEmailAPI.create(form);
      toast.success('Template created');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="create-template-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-template-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#DADCE0]">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            <Plus className="w-5 h-5 inline mr-2 text-[#1A73E8]" />New Email Template
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F1F3F4] flex items-center justify-center" data-testid="create-template-close">
            <X className="w-4 h-4 text-[#5F6368]" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Template Key (unique ID)</label>
              <input value={form.key} onChange={e => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s/g, '_') })} className="input-google text-sm font-mono" placeholder="e.g. monthly_digest" data-testid="create-key" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input-google text-sm" placeholder="Monthly Digest" data-testid="create-name" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Description</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="input-google text-sm" data-testid="create-description" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Subject</label>
            <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="input-google text-sm" data-testid="create-subject" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Variables (comma-separated)</label>
            <input
              value={(form.variables || []).join(', ')}
              onChange={e => setForm({ ...form, variables: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
              className="input-google text-sm font-mono"
              placeholder="app_name, user_name, ..."
              data-testid="create-variables"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Body (HTML)</label>
            <textarea
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              className="input-google text-sm font-mono min-h-[200px] resize-y"
              placeholder="<div>Your email HTML here...</div>"
              data-testid="create-body"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 bg-[#F1F3F4] text-[#5F6368] rounded-lg text-sm font-medium hover:bg-[#E8EAED] transition-colors">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="flex-1 btn-google flex items-center justify-center gap-2" data-testid="create-submit-btn">
              {saving ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Creating...</> : <><Plus className="w-4 h-4" /> Create Template</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailNotificationsPage;
